import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Wrench, Clock, ChatCircleDots, PaperPlaneRight, Calendar, User as UserIcon } from "@phosphor-icons/react";
import { fmtDuration } from "../lib/duration";

/**
 * BreakdownChats — per-breakdown chat threads for record-keeping.
 *  - Lists every breakdown (active + closed) with reason, description, duration.
 *  - Selecting one opens its dedicated chat thread.
 *  - Posting a message while a vessel is paused auto-tags it to the active breakdown
 *    so messages are filed under the breakdown that triggered the conversation.
 */

function fmtDT(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return `${d.toLocaleDateString()} · ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })}`;
}
function fmtHM(min) {
    return fmtDuration(min);
}

export default function BreakdownChats({ vesselId }) {
    const { user } = useAuth();
    const [breakdowns, setBreakdowns] = useState([]);
    const [selected, setSelected] = useState(null);
    const [messages, setMessages] = useState([]);
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get(`/vessels/${vesselId}/breakdowns`);
            setBreakdowns(data);
            // Auto-pick first (newest, including the active one)
            setSelected((prev) => prev || (data[0]?.breakdown_id || null));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [vesselId]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        if (!selected) {
            setMessages([]);
            return;
        }
        let cancelled = false;
        const fetchMsgs = async () => {
            try {
                const { data } = await api.get(`/vessels/${vesselId}/messages`, {
                    params: { breakdown_id: selected },
                });
                if (!cancelled) setMessages(data);
            } catch { /* ignore */ }
        };
        fetchMsgs();
        const t = setInterval(fetchMsgs, 15000);
        return () => { cancelled = true; clearInterval(t); };
    }, [vesselId, selected]);

    const send = async () => {
        if (!body.trim()) return;
        setSending(true);
        try {
            await api.post(`/vessels/${vesselId}/messages`, {
                body: body.trim(),
                breakdown_id: selected,
            });
            setBody("");
            // refresh both lists
            const { data } = await api.get(`/vessels/${vesselId}/messages`, {
                params: { breakdown_id: selected },
            });
            setMessages(data);
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    const active = breakdowns.find((b) => b.breakdown_id === selected);

    return (
        <div className="g-card overflow-hidden shadow-sm" data-testid="breakdown-chats">
            <div className="bg-[var(--grindrod-navy)] text-white px-4 py-3 flex items-center gap-3 border-b-[3px] border-[var(--grindrod-red)]">
                <Wrench size={18} weight="fill" />
                <div className="font-display font-bold uppercase tracking-wide">Breakdown Chat</div>
                <span className="ml-auto text-xs text-slate-300 tabular">{breakdowns.length} on record</span>
            </div>

            {loading ? (
                <div className="p-6 text-center text-sm text-slate-400">Loading breakdowns…</div>
            ) : breakdowns.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400" data-testid="breakdown-chats-empty">
                    No breakdowns recorded yet. When a vessel is paused, a chat thread will be opened here automatically.
                </div>
            ) : (
                <div className="grid md:grid-cols-[280px_1fr] divide-x divide-slate-100 min-h-[460px]">
                    {/* Breakdown list */}
                    <div className="bg-slate-50 max-h-[600px] overflow-y-auto" data-testid="breakdown-list">
                        {breakdowns.map((b) => (
                            <button
                                key={b.breakdown_id}
                                onClick={() => setSelected(b.breakdown_id)}
                                className={`w-full text-left p-3 border-b border-slate-100 transition-colors ${
                                    selected === b.breakdown_id ? "bg-white border-l-[3px] border-l-[var(--grindrod-red)]" : "hover:bg-white"
                                }`}
                                data-testid={`breakdown-item-${b.breakdown_id}`}
                            >
                                <div className="flex items-center gap-2">
                                    {b.is_active && (
                                        <span className="inline-flex items-center gap-1 g-pill g-pill-red animate-pulse" style={{ fontSize: 9, padding: "1px 6px" }}>
                                            ACTIVE
                                        </span>
                                    )}
                                    <div className="text-sm font-bold text-[var(--grindrod-navy)] uppercase tracking-wide truncate flex-1">
                                        {b.reason || "Breakdown"}
                                    </div>
                                </div>
                                {b.description && (
                                    <div className="text-xs text-slate-600 mt-1 line-clamp-2">{b.description}</div>
                                )}
                                <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-2 tabular">
                                    <span className="inline-flex items-center gap-1">
                                        <Calendar size={10} /> {fmtDT(b.started_at)}
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                        <Clock size={10} /> {fmtHM(b.duration_minutes)}
                                    </span>
                                </div>
                                {b.message_count > 0 && (
                                    <div className="text-[10px] text-slate-500 mt-1 inline-flex items-center gap-1">
                                        <ChatCircleDots size={10} /> {b.message_count} message{b.message_count === 1 ? "" : "s"}
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Selected thread */}
                    <div className="flex flex-col">
                        {active ? (
                            <>
                                <div className="px-4 py-3 border-b border-slate-100 bg-white">
                                    <div className="flex items-center gap-2">
                                        {active.is_active ? (
                                            <span className="g-pill g-pill-red animate-pulse" style={{ fontSize: 9, padding: "1px 6px" }}>ACTIVE</span>
                                        ) : (
                                            <span className="g-pill" style={{ fontSize: 9, padding: "1px 6px", background: "#F1F5F9", color: "#1E293B", border: "1px solid #CBD5E1" }}>CLOSED</span>
                                        )}
                                        <div className="font-display font-bold text-[var(--grindrod-navy)]">
                                            {active.reason}
                                        </div>
                                    </div>
                                    {active.description && (
                                        <div className="text-xs text-slate-600 mt-1">{active.description}</div>
                                    )}
                                    <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500 mt-2 tabular">
                                        <span className="inline-flex items-center gap-1">
                                            <Calendar size={11} /> Started {fmtDT(active.started_at)}{active.started_by_name ? ` by ${active.started_by_name}` : ""}
                                        </span>
                                        {active.ended_at && (
                                            <span className="inline-flex items-center gap-1">
                                                <Calendar size={11} /> Ended {fmtDT(active.ended_at)}{active.ended_by_name ? ` by ${active.ended_by_name}` : ""}
                                            </span>
                                        )}
                                        <span className="inline-flex items-center gap-1">
                                            <Clock size={11} /> {fmtHM(active.duration_minutes)}
                                        </span>
                                    </div>
                                </div>

                                <div
                                    className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[440px] bg-slate-50/40"
                                    data-testid="breakdown-thread"
                                >
                                    {messages.length === 0 ? (
                                        <div className="text-sm text-slate-400 text-center py-8">
                                            No messages yet for this breakdown. Be the first to post an update.
                                        </div>
                                    ) : (
                                        [...messages].reverse().map((m) => {
                                            const isMine = user && m.from_id === user.id;
                                            return (
                                                <div
                                                    key={m.id}
                                                    className={`flex flex-col ${isMine ? "items-end" : "items-start"}`}
                                                    data-testid={`bd-msg-${m.id}`}
                                                >
                                                    <div className="text-[10px] text-slate-500 mb-1 flex items-center gap-1.5 px-1">
                                                        <UserIcon size={10} />
                                                        <span className="font-semibold text-[var(--grindrod-navy)]">{m.from_name}</span>
                                                        <span className="uppercase tracking-wide">· {m.from_role}</span>
                                                        <span>· {fmtDT(m.created_at)}</span>
                                                    </div>
                                                    <div
                                                        className={`max-w-[80%] rounded-md px-3 py-2 text-sm shadow-sm ${
                                                            isMine
                                                                ? "bg-[var(--grindrod-navy)] text-white rounded-br-sm"
                                                                : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
                                                        }`}
                                                    >
                                                        {m.subject && (
                                                            <div className={`font-semibold mb-1 ${isMine ? "text-white" : "text-[var(--grindrod-navy)]"}`}>
                                                                {m.subject}
                                                            </div>
                                                        )}
                                                        <div className="whitespace-pre-wrap">{m.body}</div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                <div className="border-t border-slate-100 p-3 bg-white flex items-end gap-2">
                                    <textarea
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                                                e.preventDefault();
                                                send();
                                            }
                                        }}
                                        rows={2}
                                        placeholder={active.is_active ? "Update on this breakdown… (⌘/Ctrl + Enter to send)" : "Reply on this closed breakdown…"}
                                        className="g-input flex-1 resize-none"
                                        data-testid="breakdown-msg-input"
                                    />
                                    <button
                                        onClick={send}
                                        disabled={sending || !body.trim()}
                                        className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50"
                                        data-testid="breakdown-msg-send"
                                    >
                                        <PaperPlaneRight size={14} weight="fill" />
                                        {sending ? "Sending…" : "Send"}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-sm text-slate-400 p-8">
                                Select a breakdown on the left to open its chat thread.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
